import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import listPlugin from "@fullcalendar/list"
import interactionPlugin from "@fullcalendar/interaction"
import type { EventApi } from "@fullcalendar/core"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { api } from "@/lib/api"
import { ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { CalendarEditModal } from "@/components/portal/CalendarEditModal"
import { FortyEventModal } from "@/components/portal/FortyEventModal"
import { PlanBoard, type PlanEvent, type PlanRow } from "@/components/portal/PlanBoard"
import { CalendarCreateModal } from "@/components/portal/CalendarCreateModal"
import { NotifyModal } from "@/components/portal/NotifyModal"
import { getHolidaysInRange } from "@/lib/germanHolidays"
import { useSearchParams } from "react-router-dom"
import { getDraft, type CalendarDraft } from "@/lib/draftModals"
import { useTenantFilter } from "@/hooks/useTenantFilter"

/**
 * Kalender mit FullCalendar — Drag/Resize/Klick-Edit.
 * Nutzt /api/v1/calendar/events zum Laden und
 * /admin/calendar/api/update-event (POST) für Drag/Resize
 * und /admin/calendar/api/delete-event für Löschen.
 */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function toLocalInputStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  const h = String(d.getHours()).padStart(2, "0")
  const min = String(d.getMinutes()).padStart(2, "0")
  return `${y}-${m}-${day}T${h}:${min}`
}

// Palette für Monteur-Farben (gleich wie admin/calendar.py)
const MONTEUR_COLORS = [
  "#7baaf7", "#57bb8a", "#b39ddb", "#fdd663",
  "#f28b82", "#4fc3f7", "#f48fb1", "#aed581",
  "#ff8a65", "#66bb6a", "#ce93d8", "#fff176",
]

// #138: eigene Palette fuer Terminkategorien — kraeftiger als die Monteur-
// Farben, damit man beim Umschalten sofort sieht, dass etwas anderes gilt.
const KATEGORIE_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16",
  "#f97316", "#14b8a6", "#a855f7", "#eab308",
]

type FarbeNach = "monteur" | "kategorie"

function gespeicherteFarbwahl(): FarbeNach {
  try { return localStorage.getItem("kalender-farbe-nach") === "kategorie" ? "kategorie" : "monteur" } catch { return "monteur" }
}

/** Auswahl für den Bearbeiten-Dialog aus einem FullCalendar-Termin.
 *  Echte Termin-Zeiten haben Vorrang vor denen des angeklickten Segments
 *  (siehe echt_start/echt_end beim Mapping). */
function auswahlAus(e: EventApi) {
  const props = e.extendedProps as { echt_start?: string | null; echt_end?: string | null }
  return {
    id: e.id,
    title: e.title,
    start: props?.echt_start || e.start?.toISOString(),
    end: props?.echt_end || e.end?.toISOString() || e.start?.toISOString(),
    ...e.extendedProps,
    // #118: echtes „ganztaegig“ des Termins — mehrtaegige Termine mit Uhrzeit liegen als Balken auch in der
    // Ganztags-Zeile (allDay des Balkens = true), sind aber nicht ganztaegig.
    allDay: (e.extendedProps as { echt_ganztaegig?: boolean }).echt_ganztaegig ?? e.allDay,
  }
}

export default function CalendarView() {
  const [tenant, setTenant] = useTenantFilter()
  const qc = useQueryClient()
  const ref = useRef<FullCalendar | null>(null)
  const [selected, setSelected] = useState<any | null>(null)
  const [fortySelected, setFortySelected] = useState<any | null>(null)
  // Ansicht: klassischer Kalender (FullCalendar) oder Plantafel (Zeilen =
  // Mitarbeiter). Backend-unabhängig — gilt für HERO wie fortytools.
  const [viewKind, setViewKind] = useState<"calendar" | "board">("calendar")
  const [boardMode, setBoardMode] = useState<"week" | "day">("week")
  const [boardAnchor, setBoardAnchor] = useState<Date>(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d })
  const [msg, setMsg] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createRange, setCreateRange] = useState<{ start?: string; end?: string }>({})
  const [createPrefill, setCreatePrefill] = useState<{
    title?: string; description?: string; categoryId?: string; partnerIds?: number[]; projectId?: string
  }>({})
  const [restoreDraftId, setRestoreDraftId] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // Wiederherstellen eines minimierten Draft-Termins: ?restore_draft=<uuid>.
  // Reagiert auf searchParams-Wechsel — sonst klappt der Restore nur beim
  // ersten Mount, nicht wenn der User schon auf /calendar ist und in der
  // MinimizedDraftsBar auf eine Pill klickt (URL aendert sich, Effekt muss
  // erneut feuern).
  const restoreParam = searchParams.get("restore_draft")
  useEffect(() => {
    if (!restoreParam) return
    const draft = getDraft(restoreParam)
    if (draft && draft.kind === "calendar") {
      const d = draft as CalendarDraft
      setCreateRange({ start: d.start, end: d.end })
      setCreatePrefill({
        title: d.title,
        description: d.description,
        categoryId: d.categoryId,
        partnerIds: d.partnerIds,
        projectId: d.projectId,
      })
      setRestoreDraftId(d.id)
      setCreateOpen(true)
    }
    // Query-Param wieder entfernen (damit nicht bei jedem Reload neu geoeffnet wird)
    const next = new URLSearchParams(searchParams)
    next.delete("restore_draft")
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreParam])
  // #169: Direktlink auf einen Termin (z. B. vom KI-Telefon neben dem
  // Häkchen „Termin prüfen"): ?datum=YYYY-MM-DD&event=<id> springt auf den
  // Tag und öffnet den Termin. Danach werden beide Parameter entfernt, sonst
  // ginge der Dialog bei jedem Neuladen wieder auf.
  const zielDatum = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get("datum") || "")
    ? searchParams.get("datum") as string : undefined
  const zielEvent = searchParams.get("event")
  const [activeMonteure, setActiveMonteure] = useState<Set<number>>(new Set())
  const [showUnassigned, setShowUnassigned] = useState(true)
  const [showHolidays, setShowHolidays] = useState(true)
  // #137: Sperrzeiten (z. B. „Arman dienstags 8–12 im Büro") sichtbar machen
  const [showSperrzeiten, setShowSperrzeiten] = useState(true)
  // #138: Farbe nach Monteur (bisher) oder nach Terminkategorie
  const [farbeNach, setFarbeNachState] = useState<FarbeNach>(gespeicherteFarbwahl)
  const setFarbeNach = (f: FarbeNach) => {
    setFarbeNachState(f)
    try { localStorage.setItem("kalender-farbe-nach", f) } catch { /* privat */ }
  }
  const [legendeZu, setLegendeZu] = useState(false)
  const [range, setRange] = useState(() => {
    const now = zielDatum ? new Date(`${zielDatum}T12:00:00`) : new Date()
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0)
    return { from: start.toISOString(), to: end.toISOString() }
  })

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
  })

  const monteure = useQuery({
    queryKey: ["monteure", tenant],
    queryFn: async () => {
      if (!tenant) return { items: [] }
      const { data, error } = await api.GET("/api/v1/calendar/monteure", {
        params: { query: { tenant } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    enabled: !!tenant,
  })

  // Initialize activeMonteure to all visible when list loads
  // Key ist hero_partner_id (matcht calendar_events.partner_ids), Fallback auf id.
  useMemo(() => {
    if (monteure.data?.items) {
      setActiveMonteure(new Set(monteure.data.items.map((m) => m.hero_partner_id ?? m.id)))
    }
  }, [monteure.data])

  // Monteur → Farbe Map. Key ist hero_partner_id, damit gegen event.partner_ids gematcht werden kann.
  const colorMap = useMemo(() => {
    const m: Record<number, string> = {}
    monteure.data?.items?.forEach((mon, i) => {
      const key = mon.hero_partner_id ?? mon.id
      m[key] = MONTEUR_COLORS[i % MONTEUR_COLORS.length]
    })
    return m
  }, [monteure.data])

  // hero_partner_id → name Map (fuer Initialen-Badge bei Multi-Monteur-Events)
  const nameMap = useMemo(() => {
    const m: Record<number, string> = {}
    monteure.data?.items?.forEach((mon) => {
      const key = mon.hero_partner_id ?? mon.id
      m[key] = mon.name || ""
    })
    return m
  }, [monteure.data])

  // Kategorien fuer Farbe + Legende (#138). Gleicher Key wie das
  // Bearbeiten-Fenster, damit HERO nur einmal gefragt wird.
  const kalOptionen = useQuery<{ categories?: { id: number; name: string }[] }>({
    queryKey: ["cal-options", tenant],
    queryFn: async () => {
      const res = await fetch(`/api/v1/calendar/options?tenant=${encodeURIComponent(tenant || "")}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: !!tenant && farbeNach === "kategorie",
    staleTime: 5 * 60_000,
  })
  const kategorien = kalOptionen.data?.categories || []
  const katColorMap = useMemo(() => {
    const m: Record<number, string> = {}
    kategorien.forEach((k, i) => { m[k.id] = KATEGORIE_COLORS[i % KATEGORIE_COLORS.length] })
    return m
  }, [kategorien])

  const events = useQuery({
    queryKey: ["calendar", tenant, range.from, range.to],
    queryFn: async () => {
      if (!tenant) return { items: [] as any[] }
      const { data, error } = await api.GET("/api/v1/calendar/events", {
        params: { query: { tenant, from: range.from, to: range.to } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    enabled: !!tenant,
  })

  const fcEvents = useMemo(() => {
    const out: any[] = []
    for (const e of (events.data?.items ?? [])) {
      const monteurIds: number[] = e.partner_ids || e.monteur_ids || []
      const assigned = monteurIds.filter((id: number) => colorMap[id])

      // Filter by active monteure
      if (assigned.length > 0) {
        if (!assigned.some((id: number) => activeMonteure.has(id))) continue
      } else if (!showUnassigned) {
        continue
      }

      // Farbe: bei einem → single color, bei mehreren → Streifen-Gradient (wie Legacy).
      // is_done overridet auf grün.
      let color: string
      if (e.is_done) {
        color = "#10b981"
      } else if (farbeNach === "kategorie") {
        color = (e.category_id != null && katColorMap[e.category_id]) || "#9e9e9e"
      } else if (assigned.length === 0) {
        color = "#9e9e9e"
      } else if (assigned.length === 1) {
        color = colorMap[assigned[0]]
      } else {
        const uniqueColors = Array.from(new Set(assigned.map((id: number) => colorMap[id])))
        if (uniqueColors.length === 1) {
          color = uniqueColors[0]
        } else {
          const pct = Math.floor(100 / uniqueColors.length)
          const stops: string[] = []
          uniqueColors.forEach((c, i) => {
            stops.push(`${c} ${i * pct}%`)
            stops.push(`${c} ${(i + 1) * pct}%`)
          })
          color = `linear-gradient(135deg, ${stops.join(", ")})`
        }
      }

      const monteurNames = assigned.map((id: number) => nameMap[id] || "").filter(Boolean)
      const extendedProps = {
        description: e.description || "",
        project_match_id: e.project_match_id,
        category_id: e.category_id,
        is_done: e.is_done,
        partner_ids: e.partner_ids || [],
        // 🔴 Die echten Zeiten des Termins — NICHT die des angeklickten Balkens.
        // Mehrtaegige Termine werden unten in Segmente zerlegt, und der Balken
        // in der all-day-Zeile traegt nur ein Datum (Start 00:00). Wer daraus
        // das Bearbeiten-Formular fuellt, zeigt 00:00 statt 08:00 — und
        // schreibt das beim Uebernehmen nach HERO zurueck. Genau so verlor ein
        // Termin am 10.08.2026 seine Uhrzeit.
        echt_start: e.start_time || null,
        echt_end: e.end_time || null,
        echt_ganztaegig: !!e.all_day,
        monteur_names: monteurNames,
        color_raw: color,  // fuer eventDidMount Gradient-Apply
        recurrence_series_id: e.recurrence_series_id || null,
        forty_status: (e as any).forty_status || null,
      }

      // Multi-Day-Split wie Legacy (admin/calendar.py:281):
      // Background-Spalte pro Tag + allDay-Label oben. Events die vor 6:00 enden → letzten Tag weglassen.
      // Gilt fuer timed Multi-Day UND all-day Multi-Day (Urlaub ueber mehrere Tage).
      const startDt = e.start_time ? new Date(e.start_time) : null
      const endDt = e.end_time ? new Date(e.end_time) : null
      const isMultiDay = !!(startDt && endDt) && (startDt.toDateString() !== endDt.toDateString())

      if (isMultiDay && startDt && endDt) {
        let lastDay = new Date(endDt)
        lastDay.setHours(0, 0, 0, 0)
        if (endDt.getHours() === 0 && endDt.getMinutes() === 0) {
          // Ende exakt Mitternacht = exklusiv → letzter Tag ist der Vortag
          lastDay.setDate(lastDay.getDate() - 1)
        } else if (endDt.getHours() < 6) {
          // Endet vor slotMinTime (z.B. 02:00) → letzten Tag nicht rendern.
          // WICHTIG else-if: bei 00:00-Ende griffen frueher BEIDE Regeln →
          // Blocker wurde einen Tag zu kurz gezeichnet (Bug 09.07.2026).
          lastDay.setDate(lastDay.getDate() - 1)
        }
        const firstDay = new Date(startDt)
        firstDay.setHours(0, 0, 0, 0)
        let day = new Date(firstDay)
        let i = 0
        while (day <= lastDay) {
          const ds = new Date(day); ds.setHours(6, 0, 0, 0)
          let de: Date
          if (day.toDateString() === endDt.toDateString()) {
            de = new Date(day)
            de.setHours(Math.max(endDt.getHours(), 6), endDt.getMinutes(), 0, 0)
          } else {
            de = new Date(day); de.setHours(22, 0, 0, 0)
          }
          out.push({
            id: `${e.id}_bg${i}`,
            start: ds.toISOString(),
            end: de.toISOString(),
            display: "background",
            backgroundColor: color,
            extendedProps,
          })
          day.setDate(day.getDate() + 1)
          i++
        }
        const fmtD = (d: Date) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
        const fmtT = (d: Date) => d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
        const isAllDay = !!e.all_day
        const tr = isAllDay
          ? `${fmtD(startDt)} – ${fmtD(endDt)}`
          : `${fmtD(startDt)} ${fmtT(startDt)} – ${fmtD(endDt)} ${fmtT(endDt)}`
        // Span-Event: echter allDay-Balken ueber gesamten Zeitraum (exclusive end in FullCalendar)
        const spanEnd = new Date(lastDay); spanEnd.setDate(spanEnd.getDate() + 1)
        out.push({
          id: String(e.id),
          title: e.title || "(ohne Titel)",
          start: toLocalDateStr(startDt),
          end: toLocalDateStr(spanEnd),
          allDay: true,
          backgroundColor: color,
          borderColor: color,
          extendedProps: { ...extendedProps, time_range: tr, is_multiday: true },
        })
        continue
      }

      // Single-day all-day: allDay-Label oben + Background-Shade ueber ganzen Tag (6-22)
      if (e.all_day && startDt) {
        const dayStr = toLocalDateStr(startDt)
        const ds = new Date(startDt); ds.setHours(6, 0, 0, 0)
        const de = new Date(startDt); de.setHours(22, 0, 0, 0)
        out.push({
          id: `${e.id}_bg`,
          start: ds.toISOString(),
          end: de.toISOString(),
          display: "background",
          backgroundColor: color,
          extendedProps,
        })
        out.push({
          id: String(e.id),
          title: e.title || "(ohne Titel)",
          start: dayStr,
          allDay: true,
          backgroundColor: color,
          borderColor: color,
          extendedProps,
        })
        continue
      }

      out.push({
        id: String(e.id),
        title: e.title || "(ohne Titel)",
        start: e.start_time,
        end: e.end_time,
        allDay: e.all_day,
        backgroundColor: color,
        borderColor: color,
        extendedProps,
      })
    }
    // Deutsche Feiertage (Baden-Wuerttemberg) als allDay-Events oben + dezenter Background-Fill
    // fortytools-Einsaetze: NUR LESEN. Das Buero pflegt sie dort von Hand;
    // ohne Anzeige waere der Kalender fuer einen fortytools-Tenant leer,
    // obwohl Einsaetze existieren — und man saehe nicht, worauf der Bot
    // seine Slot-Berechnung stuetzt.
    //
    // Stand NICHT im Feiertags-Block: dort hingen die Einsaetze frueher mit
    // drin, waren also an den Feiertags-Schalter gekoppelt und verschwanden
    // mit ihm. Sie folgen demselben Monteur-Filter wie normale Termine.
    for (const fe of (events.data as any)?.forty_events ?? []) {
      const sid: number | null = fe.staff_member_id ?? null
      const bekannt = sid != null && colorMap[sid] !== undefined

      // Gleicher Filter wie bei Portal-Terminen: zugeordnet -> nur wenn der
      // Monteur aktiv ist; ohne Zuordnung -> am "ohne Zuordnung"-Schalter.
      if (bekannt) {
        if (!activeMonteure.has(sid as number)) continue
      } else if (!showUnassigned) {
        continue
      }

      const farbe = bekannt ? colorMap[sid as number] : "#64748b"
      const wer = bekannt ? nameMap[sid as number] : ""
      const titel = [wer || "Einsatz", fe.location].filter(Boolean).join(" · ")

      out.push({
        id: `forty_${fe.start_time}_${sid ?? "offen"}`,
        title: titel,   // das Symbol haengt eventDidMount an
        start: fe.start_time,
        end: fe.end_time,
        // Nicht drag/resize-bar (in fortytools gepflegt), aber anklickbar für
        // die Detailansicht. Optisch wie ein normaler Termin (gefüllt), nur mit
        // kleinem fortytools-Symbol.
        editable: false,
        durationEditable: false,
        startEditable: false,
        backgroundColor: farbe,
        borderColor: farbe,
        textColor: "#fff",
        classNames: ["fc-forty"],
        extendedProps: {
          is_forty: true,
          staff_member_id: sid,
          monteur_names: wer ? [wer] : [],
          monteur_name: (fe as any).monteur_name || wer || null,
          location: fe.location || "",
          project_id: fe.project_id ?? null,
          in_ours: (fe as any).in_ours === true,
          color_raw: farbe,
        },
      })
    }

    // #137: Sperrzeiten aus der Booking-Config als graue Hintergrund-Flaeche
    // — dieselben Fenster, in denen Telefonbot und Vorschlaege nicht buchen.
    if (showSperrzeiten) {
      for (const f of ((events.data as any)?.sperrzeiten ?? [])) {
        out.push({
          id: `sperre_${f.start}`,
          title: `🔒 ${f.grund || "Sperrzeit"}`,
          start: f.start,
          end: f.end,
          display: "background",
          backgroundColor: "rgba(100, 116, 139, 0.22)",
          classNames: ["fc-sperrzeit"],
          extendedProps: { sperrzeit: true, art: f.art },
        })
      }
    }

    if (showHolidays) {
      try {
        const from = new Date(range.from)
        const to = new Date(range.to)
        const holidays = getHolidaysInRange(from, to, "BW")
        for (const h of holidays) {
          out.push({
            id: `holiday_${h.date}`,
            title: `🎉 ${h.name}`,
            start: h.date,
            allDay: true,
            backgroundColor: "#fde68a",   // weiches gelb
            borderColor: "#f59e0b",
            textColor: "#78350f",
            editable: false,
            classNames: ["fc-holiday"],
          })
          out.push({
            id: `holiday_${h.date}_bg`,
            start: `${h.date}T06:00:00`,
            end: `${h.date}T22:00:00`,
            display: "background",
            backgroundColor: "rgba(253, 230, 138, 0.35)",
          })
        }
      } catch { /* range parse failure → skip */ }
    }

    return out
  }, [events.data, colorMap, nameMap, activeMonteure, showUnassigned, showHolidays, showSperrzeiten, farbeNach, katColorMap, range.from, range.to])

  // ── Plantafel-Daten (Zeilen = Mitarbeiter) ────────────────────────────────
  const planRows: PlanRow[] = useMemo(() => {
    const rows: PlanRow[] = (monteure.data?.items || []).map((m) => {
      const key = m.hero_partner_id ?? m.id
      return { key: String(key), name: m.name || String(key), color: colorMap[key as number] }
    })
    rows.push({ key: "__none__", name: "Ohne Zuordnung", color: "#94a3b8" })
    return rows
  }, [monteure.data, colorMap])

  const planEvents: PlanEvent[] = useMemo(() => {
    const out: PlanEvent[] = []
    for (const e of (events.data?.items ?? [])) {
      if (!e.start_time) continue
      const start = new Date(e.start_time)
      const end = e.end_time ? new Date(e.end_time) : start
      const assigned = (e.partner_ids || []).filter((id: number) => colorMap[id])
      // Monteur-Filter (wie im Kalender)
      if (assigned.length > 0) {
        if (!assigned.some((id: number) => activeMonteure.has(id))) continue
      } else if (!showUnassigned) continue
      const color = e.is_done ? "#10b981"
        : farbeNach === "kategorie" ? ((e.category_id != null && katColorMap[e.category_id]) || "#9e9e9e")
        : assigned.length === 0 ? "#9e9e9e"
        : assigned.length === 1 ? colorMap[assigned[0]]
        : colorMap[assigned[0]]  // Plantafel: Mehrfach -> je Zeile, Farbe der Zeile
      out.push({
        id: String(e.id), title: e.title || "(ohne Titel)", start, end, allDay: !!e.all_day,
        monteurKeys: assigned.map(String), color, isForty: false,
        onClick: () => setSelected({
          id: e.id, title: e.title, start: e.start_time, end: e.end_time, allDay: e.all_day,
          description: e.description || "", project_match_id: e.project_match_id,
          category_id: e.category_id, is_done: e.is_done, partner_ids: e.partner_ids || [],
          recurrence_series_id: (e as any).recurrence_series_id || null,
        }),
      })
    }
    for (const fe of ((events.data as any)?.forty_events ?? [])) {
      const sid: number | null = fe.staff_member_id ?? null
      const bekannt = sid != null && colorMap[sid] !== undefined
      if (bekannt) { if (!activeMonteure.has(sid as number)) continue }
      else if (!showUnassigned) continue
      const color = bekannt ? colorMap[sid as number] : "#64748b"
      const titel = [fe.monteur_name || "Einsatz", fe.location].filter(Boolean).join(" · ")
      out.push({
        id: `forty_${fe.start_time}_${sid ?? "offen"}`, title: titel,
        start: new Date(fe.start_time), end: new Date(fe.end_time),
        monteurKeys: bekannt ? [String(sid)] : [], color, isForty: true, inOurs: fe.in_ours === true,
        onClick: () => setFortySelected({
          start: fe.start_time, end: fe.end_time, monteur_name: fe.monteur_name,
          location: fe.location, project_id: fe.project_id, in_ours: fe.in_ours,
        }),
      })
    }
    return out
  }, [events.data, colorMap, activeMonteure, showUnassigned, farbeNach, katColorMap])

  const boardDays: Date[] = useMemo(() => {
    if (boardMode === "day") return [boardAnchor]
    const d = new Date(boardAnchor)
    const dow = (d.getDay() + 6) % 7  // Mo = 0
    const mon = new Date(d); mon.setDate(d.getDate() - dow); mon.setHours(0, 0, 0, 0)
    return Array.from({ length: 7 }, (_, i) => { const x = new Date(mon); x.setDate(mon.getDate() + i); return x })
  }, [boardAnchor, boardMode])

  const boardShift = (dir: number) => {
    setBoardAnchor((prev) => { const x = new Date(prev); x.setDate(prev.getDate() + dir * (boardMode === "day" ? 1 : 7)); return x })
  }

  // Im Plantafel-Modus feuert FullCalendars datesSet nicht -> Events-Query-Range
  // selbst an das sichtbare Fenster koppeln (mit etwas Puffer).
  useEffect(() => {
    if (viewKind !== "board" || boardDays.length === 0) return
    const from = new Date(boardDays[0]); from.setDate(from.getDate() - 1)
    const to = new Date(boardDays[boardDays.length - 1]); to.setDate(to.getDate() + 2)
    setRange({ from: from.toISOString(), to: to.toISOString() })
  }, [viewKind, boardMode, boardAnchor, boardDays])

  // Post-Drag Benachrichtigung (Kunde ueber Terminaenderung informieren)
  const [notifyCtx, setNotifyCtx] = useState<null | {
    tenant: string; projectId: string; title: string; start: string; end: string; description: string
  }>(null)
  const [notifyToast, setNotifyToast] = useState<null | {
    tenant: string; projectId: string; title: string; start: string; end: string
  }>(null)

  const updateEvent = useMutation({
    mutationFn: async (vars: { id: string; start: string; end: string; title?: string; all_day?: boolean }) => {
      const res = await fetch("/api/v1/calendar/events", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tenant, event_id: vars.id, start: vars.start, end: vars.end, title: vars.title,
                               all_day: vars.all_day }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar", tenant] })
      // Msg nur setzen wenn nicht von handleMove schon "✓ Termin verschoben" gesetzt wurde
      setMsg((prev) => prev && prev.includes("verschoben") ? prev : "✓ Gespeichert")
      setTimeout(() => {
        // Nur auto-hide wenn kein Notify-Toast offen
        setMsg((prev) => {
          if (prev === "✓ Gespeichert") return null
          return prev
        })
      }, 2000)
    },
    onError: (e) => { setMsg(`Fehler: ${e instanceof Error ? e.message : "unbekannt"}`) },
  })

  // Haendler fuer Drag/Resize: erst speichern, DANN „Kunde benachrichtigen“ anbieten.
  //
  // 🔴 Bis 30.09.2026 kam das Angebot sofort beim Loslassen — noch bevor HERO
  // das Speichern bestaetigt hatte. Scheiterte es (HERO kurz weg), konnte man
  // trotzdem eine Bestaetigung fuer eine Zeit verschicken, die nirgends stand
  // (derselbe Fehler wie beim Termin Testa, #142). Jetzt: Angebot erst nach
  // Erfolg; bei Fehler springt der Termin zurueck und es gibt kein Angebot.
  // #169: Termin aus dem Direktlink öffnen, sobald er geladen ist.
  useEffect(() => {
    if (!zielEvent || !events.isSuccess || events.isFetching) return
    const kalender = ref.current?.getApi()
    if (!kalender) return  // Plantafel offen — Link wartet auf die Kalenderansicht
    const ev = kalender.getEventById(zielEvent)
    if (ev) setSelected(auswahlAus(ev))
    else toast("Termin nicht gefunden — evtl. verschoben oder gelöscht.", "error")
    const next = new URLSearchParams(searchParams)
    next.delete("event")
    next.delete("datum")
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zielEvent, events.isSuccess, events.isFetching, fcEvents])

  const handleMove = (info: any) => {
    const pid = info.event.extendedProps?.project_match_id
    const start = info.event.start?.toISOString()
    // Ganztaegig ins Zeitraster gezogen: FullCalendar liefert kein Ende → eine Stunde statt Dauer 0 (#118)
    const ohneEnde = !info.event.end && !info.event.allDay && info.event.start
      ? new Date(info.event.start.getTime() + 60 * 60 * 1000) : null
    const end = (info.event.end || ohneEnde || info.event.start)?.toISOString()
    if (!start || !end) return
    setNotifyToast(null)
    setMsg("Speichert …")
    // #118: aus der Ganztags-Zeile ins Zeitraster gezogen (oder umgekehrt) → „ganztaegig“ mitschicken.
    // Sonst nichts mitschicken: mehrtaegige Termine mit Uhrzeit liegen auch oben, sind aber nicht ganztaegig.
    const wechsel = info.oldEvent && info.oldEvent.allDay !== info.event.allDay
    updateEvent.mutate({ id: info.event.id, start, end, all_day: wechsel ? info.event.allDay : undefined }, {
      onSuccess: () => {
        if (!pid) return
        setNotifyToast({
          tenant,
          projectId: String(pid),
          title: info.event.title || "Termin",
          start, end,
        })
        // Msg bleibt sichtbar solange notifyToast offen (wird dort geleert)
        setMsg("✓ Termin verschoben")
      },
      onError: () => {
        try { info.revert?.() } catch { /* Ansicht laedt ohnehin neu */ }
      },
    })
  }

  if (events.error) return <div className="p-10"><ErrorBox error={events.error} /></div>

  return (
    <div className="px-10 py-8 max-w-[1600px]">
      <div className="pp-page-header">
        <h2>Kalender</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {msg && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "#dbeafe", color: "#1e3a8a",
              padding: "6px 12px", borderRadius: 6,
              fontSize: 12.5, fontWeight: 600,
              border: "1px solid #93c5fd",
            }}>
              <span>{msg}</span>
              {notifyToast && (
                <button
                  type="button"
                  onClick={() => {
                    setNotifyCtx({
                      tenant: notifyToast.tenant,
                      projectId: notifyToast.projectId,
                      title: notifyToast.title,
                      start: notifyToast.start,
                      end: notifyToast.end,
                      description: "",
                    })
                    setNotifyToast(null)
                    setMsg(null)
                  }}
                  style={{
                    background: "#1e40af", color: "#fff", border: "none",
                    padding: "3px 10px", borderRadius: 4, cursor: "pointer",
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  Kunde benachrichtigen
                </button>
              )}
            </div>
          )}
          <select
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            style={{ width: "auto" }}
          >
            <option value="">Tenant wählen...</option>
            {tenantsDd.data?.items?.map((t) => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>
          {tenant && (
            <div style={{ display: "inline-flex", border: "1px solid var(--border-color)", borderRadius: 6, overflow: "hidden" }}>
              <button
                className="pp-btn pp-btn-sm"
                style={{ borderRadius: 0, border: "none", background: viewKind === "calendar" ? "var(--accent-color)" : "transparent", color: viewKind === "calendar" ? "#fff" : undefined }}
                onClick={() => setViewKind("calendar")}
              >Kalender</button>
              <button
                className="pp-btn pp-btn-sm"
                style={{ borderRadius: 0, border: "none", borderLeft: "1px solid var(--border-color)", background: viewKind === "board" ? "var(--accent-color)" : "transparent", color: viewKind === "board" ? "#fff" : undefined }}
                onClick={() => setViewKind("board")}
              >Plantafel</button>
            </div>
          )}
          <button className="pp-btn pp-btn-sm" onClick={() => events.refetch()} disabled={events.isFetching}>
            Aktualisieren
          </button>
          {tenant && (
            <button
              className="pp-btn pp-btn-primary pp-btn-sm"
              onClick={() => { setCreateRange({}); setCreateOpen(true) }}
            >
              + Neuer Termin
            </button>
          )}
        </div>
      </div>

      {!tenant ? (
        <div className="p-10 text-center text-muted-foreground rounded-md bg-card border">
          Bitte Tenant wählen.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16 }}>
          {/* Monteur-Sidebar links. In der Plantafel-Wochenansicht ist Breite
              die knappe Ressource: 200 px Legende plus Abstand kosten genau
              den Sonntag. Deshalb einklappbar — der Knopf steht auch im
              eingeklappten Zustand da, damit man zurueckfindet. */}
          {legendeZu ? (
            <button className="pp-btn pp-btn-sm" onClick={() => setLegendeZu(false)}
              title="Mitarbeiter-Auswahl einblenden"
              style={{ alignSelf: "flex-start", flexShrink: 0, padding: "6px 8px" }}>›</button>
          ) : (
          <div style={{ width: 200, flexShrink: 0 }}>
            <div className="pp-card" style={{ padding: 14, marginBottom: 0 }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
                <button className="pp-btn pp-btn-sm" onClick={() => setLegendeZu(true)}
                  title="Ausblenden — schafft Platz für die ganze Woche"
                  style={{ padding: "2px 6px", fontSize: 12 }}>‹</button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={Object.keys(colorMap).length > 0 && activeMonteure.size === monteure.data?.items?.length}
                  onChange={(e) => {
                    if (e.target.checked) setActiveMonteure(new Set(monteure.data?.items?.map(m => m.hero_partner_id ?? m.id) || []))
                    else setActiveMonteure(new Set())
                  }}
                />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--accent-color)" }} />
                <strong style={{ fontSize: 13 }}>{tenantsDd.data?.items?.find(t => t.slug === tenant)?.name || tenant}</strong>
              </div>
              {monteure.data?.items?.map((m, idx) => {
                const color = MONTEUR_COLORS[idx % MONTEUR_COLORS.length]
                const key = m.hero_partner_id ?? m.id
                const checked = activeMonteure.has(key)
                return (
                  <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginBottom: 6, paddingLeft: 20, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const n = new Set(activeMonteure)
                        if (checked) n.delete(key); else n.add(key)
                        setActiveMonteure(n)
                      }}
                    />
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
                    {m.name}
                  </label>
                )
              })}
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginTop: 8, paddingLeft: 20, cursor: "pointer" }}>
                <input type="checkbox" checked={showUnassigned} onChange={(e) => setShowUnassigned(e.target.checked)} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#9e9e9e" }} />
                <em style={{ color: "var(--text-muted)" }}>Ohne Zuordnung</em>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginTop: 8, paddingLeft: 20, cursor: "pointer" }}>
                <input type="checkbox" checked={showHolidays} onChange={(e) => setShowHolidays(e.target.checked)} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fde68a", border: "1px solid #f59e0b" }} />
                <em style={{ color: "var(--text-muted)" }}>Feiertage (BW)</em>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginTop: 8, paddingLeft: 20, cursor: "pointer" }}
                title="Wiederkehrende Sperrzeiten und Buchungssperren aus der Booking-Config — dort bucht der Telefonbot nicht">
                <input type="checkbox" checked={showSperrzeiten} onChange={(e) => setShowSperrzeiten(e.target.checked)} data-testid="sperrzeiten-schalter" />
                <span style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(100, 116, 139, 0.35)", border: "1px solid #64748b" }} />
                <em style={{ color: "var(--text-muted)" }}>Sperrzeiten</em>
              </label>

              {/* #138: Farbe nach Monteur oder Terminkategorie */}
              <div style={{ marginTop: 14, fontSize: 12, color: "var(--text-muted)" }}>Farbe nach</div>
              <div style={{ display: "flex", gap: 4, marginTop: 4 }} role="group" aria-label="Farbe nach">
                {(["monteur", "kategorie"] as const).map((f) => (
                  <button key={f} type="button" className="pp-btn pp-btn-sm"
                    data-testid={`farbe-nach-${f}`}
                    aria-pressed={farbeNach === f}
                    onClick={() => setFarbeNach(f)}
                    style={{
                      flex: 1, fontSize: 12, padding: "3px 6px",
                      background: farbeNach === f ? "var(--accent-color)" : undefined,
                      color: farbeNach === f ? "#fff" : undefined,
                    }}>
                    {f === "monteur" ? "Monteur" : "Kategorie"}
                  </button>
                ))}
              </div>
              {farbeNach === "kategorie" && (
                <div style={{ marginTop: 8 }} data-testid="kategorie-legende">
                  {kalOptionen.isLoading && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Kategorien werden geladen …</div>}
                  {kalOptionen.isError && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Kategorien nicht abrufbar.</div>}
                  {kategorien.map((k) => (
                    <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginBottom: 4, paddingLeft: 20 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: katColorMap[k.id], flexShrink: 0 }} />
                      {k.name}
                    </div>
                  ))}
                  {kategorien.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, paddingLeft: 20 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#9e9e9e", flexShrink: 0 }} />
                      <em style={{ color: "var(--text-muted)" }}>ohne Kategorie</em>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Kalender / Plantafel */}
          <div className="bg-card rounded-lg border shadow-xs p-3 fc-portal" style={{ flex: 1, minWidth: 0 }}>
          {viewKind === "board" ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <button className="pp-btn pp-btn-sm" onClick={() => setBoardAnchor(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d })}>Heute</button>
                <button className="pp-btn pp-btn-sm" onClick={() => boardShift(-1)}>‹</button>
                <button className="pp-btn pp-btn-sm" onClick={() => boardShift(1)}>›</button>
                <strong style={{ fontSize: 14, margin: "0 8px" }}>
                  {boardMode === "day"
                    ? boardAnchor.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
                    : `${boardDays[0].toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} – ${boardDays[6].toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}`}
                </strong>
                <div style={{ marginLeft: "auto", display: "inline-flex", border: "1px solid var(--border-color)", borderRadius: 6, overflow: "hidden" }}>
                  <button className="pp-btn pp-btn-sm" style={{ borderRadius: 0, border: "none", background: boardMode === "day" ? "var(--accent-color)" : "transparent", color: boardMode === "day" ? "#fff" : undefined }} onClick={() => setBoardMode("day")}>Tag</button>
                  <button className="pp-btn pp-btn-sm" style={{ borderRadius: 0, border: "none", borderLeft: "1px solid var(--border-color)", background: boardMode === "week" ? "var(--accent-color)" : "transparent", color: boardMode === "week" ? "#fff" : undefined }} onClick={() => setBoardMode("week")}>Woche</button>
                </div>
              </div>
              <PlanBoard rows={planRows} events={planEvents} mode={boardMode} days={boardDays} />
            </div>
          ) : (
          <FullCalendar
            ref={ref}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            initialDate={zielDatum}
            locale="de"
            firstDay={1}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            buttonText={{ today: "Heute", month: "month", week: "week", day: "day", list: "Liste" }}
            events={fcEvents}
            editable
            selectable
            selectMirror
            droppable={false}
            height="auto"
            select={(info) => {
              setCreateRange({
                start: toLocalInputStr(info.start),
                end: toLocalInputStr(info.end),
              })
              setCreateOpen(true)
            }}
            allDaySlot={true}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            nowIndicator
            eventDidMount={(info) => {
              // Termintitel tragen Kundennamen — „Problem melden" schwärzt sie
              // im Bildschirmfoto (Baustein 17).
              info.el.querySelectorAll(".fc-event-title, .fc-event-main, .fc-list-event-title")
                .forEach((el) => el.setAttribute("data-pii", ""))
              // Multi-Monteur: Gradient-Hintergrund auf DOM legen (FullCalendar kann nur flat color).
              const c = (info.event.extendedProps as any)?.color_raw || ""
              if (typeof c === "string" && c.indexOf("linear-gradient") === 0) {
                info.el.style.background = c
                info.el.style.backgroundColor = "transparent"
                info.el.style.borderColor = "transparent"
              }
              // Multi-Day: Zeitraum als zweite Zeile unter Titel anhaengen
              const tr = (info.event.extendedProps as any)?.time_range as string | undefined
              const isMulti = (info.event.extendedProps as any)?.is_multiday === true
              if (tr && isMulti) {
                const titleEl = info.el.querySelector(".fc-event-title")
                if (titleEl && !info.el.querySelector(".fc-event-range")) {
                  const rangeEl = document.createElement("div")
                  rangeEl.className = "fc-event-range"
                  rangeEl.style.cssText = "font-size:10px;opacity:0.9;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px"
                  rangeEl.textContent = tr
                  titleEl.parentElement?.appendChild(rangeEl)
                }
              }
              // Tooltip: voller Titel + Zeitraum (fuer abgeschnittene Zellen)
              info.el.title = tr ? `${info.event.title}\n${tr}` : info.event.title
              // fortytools-Einsatz: sieht aus wie ein normaler Termin (gefüllt),
              // nur mit kleinem fortytools-Symbol. ✓ = auch in UNSEREM Kalender
              // (in beiden), sonst neutrales „40T" (nur in fortytools).
              if ((info.event.extendedProps as any)?.is_forty === true) {
                const inOurs = (info.event.extendedProps as any)?.in_ours === true
                const fWer = (info.event.extendedProps as any)?.monteur_name
                  || ((info.event.extendedProps as any)?.monteur_names || [])[0]
                const fOrt = (info.event.extendedProps as any)?.location
                info.el.title = [
                  inOurs ? "In fortytools UND unserem Kalender" : "Einsatz aus fortytools",
                  fWer ? `Monteur: ${fWer}` : "Ohne Monteur-Zuordnung",
                  fOrt ? `Ort: ${fOrt}` : null,
                  "Klicken für Details",
                ].filter(Boolean).join("\n")
                const t0 = info.el.querySelector(".fc-event-title")
                if (t0 && !info.el.querySelector(".fc-forty-tag")) {
                  const tag = document.createElement("span")
                  tag.className = "fc-forty-tag"
                  tag.style.cssText =
                    "display:inline-block;font-size:9px;color:#fff;padding:1px 4px;" +
                    "border-radius:3px;line-height:1.2;margin-left:4px;vertical-align:middle;" +
                    "background:" + (inOurs ? "rgba(16,185,129,0.95)" : "rgba(0,0,0,0.35)")
                  tag.textContent = inOurs ? "✓ 40T" : "40T"
                  t0.appendChild(tag)
                }
                return
              }
              // fortytools-Uebertragungsstatus: das Buero traegt Termine dort
              // von Hand nach — im Kalender muss sofort sichtbar sein, was noch
              // offen ist. Nur bei fortytools-Tenants gesetzt; bei HERO ist das
              // Feld null (Backend schreibt selbst) und es erscheint nichts.
              const fs = (info.event.extendedProps as any)?.forty_status as string | null
              if (fs === "uebertragen" || fs === "offen") {
                const uebertragen = fs === "uebertragen"
                const badge = document.createElement("span")
                badge.style.cssText =
                  "display:inline-block;font-size:9px;padding:1px 4px;border-radius:3px;" +
                  "line-height:1.2;margin-left:4px;vertical-align:middle;font-weight:600;" +
                  (uebertragen
                    ? "background:rgba(16,185,129,0.9);color:#fff"
                    : "background:rgba(245,158,11,0.95);color:#111")
                badge.textContent = uebertragen ? "\u2713 40T" : "\u26A0 offen"
                badge.title = uebertragen
                  ? "In fortytools vorhanden"
                  : "Noch NICHT in fortytools eingetragen \u2014 bitte nachpflegen"
                const t = info.el.querySelector(".fc-event-title") || info.el.querySelector(".fc-event-main")
                if (t) t.appendChild(badge)
                else info.el.appendChild(badge)
                if (!uebertragen) info.el.style.outline = "2px solid rgba(245,158,11,0.85)"
              }
              // Initialen-Badge bei >1 Monteur (z.B. "A+K").
              const names: string[] = (info.event.extendedProps as any)?.monteur_names || []
              if (names.length > 1) {
                const initials = names.map((n) => (n || "").trim().charAt(0).toUpperCase()).filter(Boolean).join("+")
                const tag = document.createElement("span")
                tag.style.cssText = "display:inline-block;font-size:9px;background:rgba(0,0,0,0.3);color:#fff;padding:1px 4px;border-radius:3px;line-height:1.2;margin-left:4px;vertical-align:middle"
                tag.textContent = initials
                const titleEl = info.el.querySelector(".fc-event-title") || info.el.querySelector(".fc-event-main")
                if (titleEl) titleEl.appendChild(tag)
                else info.el.appendChild(tag)
              }
            }}
            eventClick={(info) => {
              const e = info.event
              if (String(e.id).startsWith("holiday_")) return  // Feiertage sind nicht editierbar
              if (String(e.id).startsWith("forty_")) {
                // fortytools-Einsatz: read-only Detailansicht (Kunde/Objekt/Monteur/Ort)
                setFortySelected({
                  start: e.start?.toISOString(),
                  end: e.end?.toISOString() || e.start?.toISOString(),
                  monteur_name: (e.extendedProps as any)?.monteur_name || null,
                  location: (e.extendedProps as any)?.location || null,
                  project_id: (e.extendedProps as any)?.project_id ?? null,
                  in_ours: (e.extendedProps as any)?.in_ours === true,
                })
                return
              }
              setSelected(auswahlAus(e))
            }}
            eventDrop={handleMove}
            eventResize={handleMove}
            datesSet={(ds) => {
              setRange({ from: ds.start.toISOString(), to: ds.end.toISOString() })
            }}
          />
          )}
          </div>
        </div>
      )}

      <FortyEventModal
        open={!!fortySelected}
        onClose={() => setFortySelected(null)}
        tenant={tenant}
        event={fortySelected}
      />

      <CalendarEditModal
        open={!!selected}
        onClose={() => setSelected(null)}
        tenant={tenant}
        event={selected}
        onCopy={(data) => {
          setSelected(null)
          setCreateRange({ start: undefined, end: undefined })
          setCreatePrefill(data)
          setCreateOpen(true)
        }}
      />
      <CalendarCreateModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setCreatePrefill({}); setRestoreDraftId(null) }}
        tenant={tenant}
        initialStart={createRange.start}
        initialEnd={createRange.end}
        initialTitle={createPrefill.title}
        initialDescription={createPrefill.description}
        initialCategoryId={createPrefill.categoryId}
        initialPartnerIds={createPrefill.partnerIds}
        initialProjectId={createPrefill.projectId}
        draftId={restoreDraftId || undefined}
      />
      {notifyCtx && (
        <NotifyModal
          open={!!notifyCtx}
          onClose={() => setNotifyCtx(null)}
          tenant={notifyCtx.tenant}
          projectId={notifyCtx.projectId}
          title={notifyCtx.title}
          start={notifyCtx.start}
          end={notifyCtx.end}
          description={notifyCtx.description}
          isChange={true}
        />
      )}
      {notifyToast && (
        <div
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 1200,
            background: "#1e293b", color: "#fff",
            padding: "14px 18px", borderRadius: 10,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            display: "flex", alignItems: "center", gap: 14,
            fontSize: 13, minWidth: 340,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Termin verschoben</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Kunden per E-Mail informieren?</div>
          </div>
          <button
            type="button"
            onClick={() => {
              setNotifyCtx({
                tenant: notifyToast.tenant,
                projectId: notifyToast.projectId,
                title: notifyToast.title,
                start: notifyToast.start,
                end: notifyToast.end,
                description: "",
              })
              setNotifyToast(null)
            }}
            style={{
              background: "#3c4e7e", color: "#fff", border: "none",
              padding: "6px 14px", borderRadius: 6, cursor: "pointer",
              fontSize: 12, fontWeight: 600,
            }}
          >
            Kunde benachrichtigen
          </button>
          <button
            type="button"
            onClick={() => { setNotifyToast(null); setMsg(null) }}
            title="Ignorieren"
            style={{
              background: "transparent", color: "#cbd5e1", border: "none",
              cursor: "pointer", fontSize: 18, padding: "2px 6px",
            }}
          >×</button>
        </div>
      )}
      <p className="text-[11.5px] text-muted-foreground mt-3">
        Drag zum Verschieben, rechte Kante ziehen zum Resize. Klick öffnet Edit-Modal.
      </p>
    </div>
  )
}
